/* La Caza Cali — light preview (on/off switch) component.
 *
 * Enhances every [data-light-preview] root (see snippets/lacaza-light-preview.liquid).
 * No dependencies.
 *
 * Public API:
 *   window.LacazaLightPreview.setPair(rootEl, { on, off, alt })
 *     Swaps the on/off image pair on an already-initialized root (used by
 *     assets/lacaza-product.js when the PDP variant changes). `on`/`off`
 *     are full Shopify CDN image URLs (any width — a 600/900/1254 srcset
 *     is derived from the width query param). `alt` is the base label
 *     ("<Product> — <Finish>"); " — illuminated..."/" — switched off..."
 *     is appended the same way the Liquid snippet does it.
 *
 * Analytics:
 *   window.dataLayer.push({ event: 'light_preview_toggle', to: 'off'|'on', variant })
 *   window.dataLayer.push({ event: 'light_preview_view', variant })  — fires once,
 *     when >=50% of the component has been visible (IntersectionObserver).
 */
(function () {
  'use strict';

  function pushDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function widthVariants(url) {
    if (!url) return { w600: url, w900: url, w1254: url };
    if (/([?&])width=\d+/.test(url)) {
      return {
        w600: url.replace(/([?&])width=\d+/, '$1width=600'),
        w900: url.replace(/([?&])width=\d+/, '$1width=900'),
        w1254: url.replace(/([?&])width=\d+/, '$1width=1254'),
      };
    }
    return { w600: url, w900: url, w1254: url };
  }

  function applyImage(imgEl, url, altSuffix, altBase) {
    if (!imgEl || !url) return;
    var v = widthVariants(url);
    imgEl.src = v.w1254;
    imgEl.srcset = v.w600 + ' 600w, ' + v.w900 + ' 900w, ' + v.w1254 + ' 1254w';
    if (altBase) imgEl.alt = altBase + altSuffix;
  }

  function setState(root, state, fireEvent) {
    var btn = root.querySelector('.lp__switch');
    root.dataset.state = state;
    if (btn) btn.setAttribute('aria-checked', state === 'on' ? 'true' : 'false');
    // Round 6.2: mirror onto the folded-knob button (PDP overlay fold).
    var knob = root.querySelector('.lp__knob-fold');
    if (knob) knob.setAttribute('aria-checked', state === 'on' ? 'true' : 'false');
    if (fireEvent) {
      pushDataLayer({ event: 'light_preview_toggle', to: state, variant: root.dataset.variant || null });
    }
  }

  function armSwitch(root) {
    var btn = root.querySelector('.lp__switch');
    var offImg = root.querySelector('.lp__img--off');
    if (!btn) return;
    btn.setAttribute('disabled', 'disabled');
    root.classList.add('lp--pending');

    var settled = false;
    function enable() {
      if (settled) return;
      settled = true;
      clearTimeout(safety);
      btn.removeAttribute('disabled');
      root.classList.remove('lp--pending');
    }

    if (!offImg) { enable(); return; }

    // Safety net: the off image is loading="lazy" by design (it's the
    // second/hidden image in the pair), and decode() on a not-yet-loading
    // lazy <img> can sit pending indefinitely in some browsers instead of
    // forcing the fetch. Never leave a real tap unable to reach the
    // control — force-enable after a short wait no matter what.
    var safety = setTimeout(enable, 1500);

    if (offImg.complete && offImg.naturalWidth > 0) {
      enable();
    } else if (typeof offImg.decode === 'function') {
      offImg.decode().then(enable).catch(enable);
    } else {
      offImg.addEventListener('load', enable, { once: true });
      offImg.addEventListener('error', enable, { once: true });
    }
  }

  function watchView(root) {
    if (root.dataset.lpViewBound === '1') return;
    root.dataset.lpViewBound = '1';
    if (!('IntersectionObserver' in window)) return;
    var fired = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!fired && entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          fired = true;
          pushDataLayer({ event: 'light_preview_view', variant: root.dataset.variant || null });
          io.disconnect();
        }
      });
    }, { threshold: [0, 0.5, 1] });
    io.observe(root);
  }

  function bindRoot(root) {
    if (!root || root.dataset.lpBound === '1') return;
    root.dataset.lpBound = '1';
    root.classList.add('lp--js');

    var btn = root.querySelector('.lp__switch');

    armSwitch(root);
    watchView(root);

    if (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (btn.hasAttribute('disabled')) return;
        var next = root.dataset.state === 'off' ? 'on' : 'off';
        setState(root, next, true);
      });
      // Space/Enter already produce a native `click` on a <button>; the
      // handler above covers it. We still stop the keydown from bubbling
      // so a parent gallery/keyboard-swipe handler never sees it.
      btn.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'Spacebar') {
          e.stopPropagation();
        }
      });
    }
  }

  function setPair(root, pair) {
    if (!root || !pair) return;
    var onImg = root.querySelector('.lp__img--on');
    var offImg = root.querySelector('.lp__img--off');
    applyImage(onImg, pair.on, ' — illuminated in a styled room', pair.alt);
    applyImage(offImg, pair.off, ' — switched off in the same room', pair.alt);
    var btn = root.querySelector('.lp__switch');
    var ariaSuffix = root.dataset.ariaSuffix || ' — light preview';
    if (btn && pair.alt) btn.setAttribute('aria-label', pair.alt + ariaSuffix);
    if (pair.variant !== undefined && pair.variant !== null) root.dataset.variant = pair.variant;
    // Preserve the visitor's ON/OFF choice across a finish change instead
    // of always snapping back to ON — a shopper who switched a lamp off
    // to compare the room, then picked a different finish chip, expects
    // the new finish to load already off (round 4).
    var preservedState = root.dataset.state === 'off' ? 'off' : 'on';
    setState(root, preservedState, false);
    armSwitch(root);
  }

  function init() {
    document.querySelectorAll('[data-light-preview]').forEach(bindRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LacazaLightPreview = window.LacazaLightPreview || {};
  window.LacazaLightPreview.setPair = setPair;
  window.LacazaLightPreview.init = init;
})();
